import { useEffect, useRef, useState } from "react";
import {
  buildPaymentBreakdown,
  calculatePaymentAmountSummary,
} from "../payment/paymentCalculations";
import {
  confirmRegistration,
  createPaymentOrder,
  saveConferenceRegistration,
  savePaymentWithBreakdown,
} from "../api/conferenceRegistrationApi";
import {
  ensureRegistrationForFailedPayment,
  ensureRegistrationForPaymentHandler,
  saveRegistrationBeforePayment,
} from "../payment/paymentRegistration";
import {
  buildPaymentOrderPayload,
  createValidatedPaymentOrder,
  logPaymentBreakdownSummary,
} from "../payment/paymentOrder";
import {
  buildRazorpayOptions,
  loadRazorpayScript,
} from "../payment/razorpayCheckout";
import { saveSuccessfulPaymentWithBreakdown } from "../payment/paymentResultPersistence";
import { handlePaymentConfirmationResult } from "../payment/paymentConfirmation";
import { saveFailedPayment } from "../payment/failedPaymentPersistence";
import {
  isPostPaymentAbstractEnabled,
  POST_PAYMENT_ABSTRACT_FORM_STEP,
} from "../registration/registrationModules";

export const useRegistrationPayment = ({
  razorpayKeyId,
  registrationBaseUrl,
  conferenceRegistrationUrl,
  orderUrl,
  paymentSaveUrl,
  formData,
  setFormData,
  step,
  setStep,
  genderOptions,
  validateAllMandatoryFields,
  buildConferenceRegistrationPayload,
  isPaymentUpdate,
  isCurrentRegistrationPaid,
  calculateOutstandingAmounts,
  registrationFee,
  accompanyingPersons,
  accompanyingPersonFee,
  selectedFocusedSessions,
  focusedSessionFees,
  focusedSessions,
  conveyanceFeePercentage,
  hasPostPaymentCategoryOrTypeChange,
  shouldApplyPaperDiscount,
  paperDiscountPercentage,
  currentPaperSequence,
  abstractStepNumber,
  checkExistingRegistration,
  conferenceName,
}) => {
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentErrorShown, setPaymentErrorShown] = useState(false);

  const razorpayInstanceRef = useRef(null);
  const paymentFailureHandledRef = useRef(false);
  const razorpayOpenRef = useRef(false);

  useEffect(() => {
    if (step === 7 && paymentInitiated) {
      setPaymentInitiated(false);
    }
  }, [step, paymentInitiated]);

  const isProduction = razorpayKeyId && razorpayKeyId.startsWith("rzp_live_");

  const captureFailedPayment = async (error, orderData) => {
    try {
      let registrationId = formData.id;
      let registrationDbId = formData.regId;

      if (!registrationId) {
        try {
          const conferenceRegistrationData = buildConferenceRegistrationPayload();
          const regSaveResult = await ensureRegistrationForFailedPayment({
            conferenceRegistrationUrl,
            payload: conferenceRegistrationData,
            formData,
            saveConferenceRegistration,
          });

          if (regSaveResult.ok) {
            const savedReg = regSaveResult.savedRegistration;
            registrationId = regSaveResult.registrationId;
            registrationDbId = regSaveResult.registrationDbId;

            setFormData((prev) => ({
              ...prev,
              id: savedReg.registrationId,
              regId: savedReg.dbId || savedReg.id,
            }));
          }
        } catch (regError) {
          console.warn(
            "Could not save registration for failed payment:",
            regError
          );
        }
      }

      await saveFailedPayment({
        paymentSaveUrl,
        error,
        orderData,
        registrationDbId,
        formData,
        savePaymentWithBreakdown,
      });
    } catch (captureError) {
      console.error("Error capturing failed payment:", captureError);
    }
  };

  const startPayment = async () => {
    setPaymentInitiated(true);
    setPaymentErrorShown(false);
    paymentFailureHandledRef.current = false;
    razorpayOpenRef.current = false;

    const missingFields = [];
    if (!formData.title || !formData.title.trim()) missingFields.push("Title");
    if (!formData.firstName.trim()) missingFields.push("First Name");
    if (!formData.lastName.trim()) missingFields.push("Last Name");
    if (!formData.email.trim()) missingFields.push("Email");
    if (!formData.mobile.trim()) missingFields.push("Mobile");
    if (!formData.country.trim()) missingFields.push("Country");
    if (genderOptions.length > 0 && (!formData.gender || !formData.gender.trim())) {
      missingFields.push("Gender");
    }

    if (missingFields.length > 0) {
      alert(
        `Please fill in the following required fields: ${missingFields.join(
          ", "
        )}`
      );
      setStep(1);
      setPaymentInitiated(false);
      return;
    }

    if (!validateAllMandatoryFields()) {
      alert("Please correct the highlighted contact details.");
      setStep(1);
      setPaymentInitiated(false);
      return;
    }

    setProcessingPayment(true);

    if (formData.registrationLookupPending || formData.registrationLookupError) {
      alert("Please reload and verify your registration before paying.");
      setProcessingPayment(false);
      setPaymentInitiated(false);
      return;
    }
    let registrationId = formData.id;
    let registrationDbId = formData.regId;

    if (!registrationId) {
      try {
        const conferenceRegistrationData = buildConferenceRegistrationPayload({
          includeAccompanyingPersons: true,
          includeIsaMembershipNumber: true,
        });

        const regSaveResult = await saveRegistrationBeforePayment({
          conferenceRegistrationUrl,
          payload: conferenceRegistrationData,
          formData,
          saveConferenceRegistration,
        });

        if (!regSaveResult.ok) {
          const regError = regSaveResult.errorMessage;
          console.error("Registration save failed:", regError);
          alert(
            `Registration failed: ${regError}. Please fix the issues and try again.`
          );
          setProcessingPayment(false);
          setPaymentInitiated(false);
          setStep(1);
          return;
        }

        const savedReg = regSaveResult.savedRegistration;
        registrationId = regSaveResult.registrationId;
        registrationDbId = regSaveResult.registrationDbId;

        setFormData((prev) => ({
          ...prev,
          id: savedReg.registrationId,
          regId: savedReg.dbId || savedReg.id,
        }));

        const paymentActionMessage = isPaymentUpdate ? "updated" : "saved";
        alert(
          `Registration ${paymentActionMessage} successfully! Registration ID: ${registrationId}. Proceeding to payment...`
        );
      } catch (regError) {
        console.error("Error saving registration before payment:", regError);
        alert(`Registration failed: ${regError.message}. Please try again.`);
        setProcessingPayment(false);
        setPaymentInitiated(false);
        setStep(1);
        return;
      }
    }

    const isAdditionalPayment = isCurrentRegistrationPaid;
    const outstandingSummary = isAdditionalPayment
      ? calculateOutstandingAmounts()
      : null;
    const { currency, amountInSmallestUnit, actualAmount } =
      calculatePaymentAmountSummary({
        isAdditionalPayment,
        outstandingSummary,
        formData,
        registrationFee,
        accompanyingPersons,
        accompanyingPersonFee,
        selectedFocusedSessions,
        focusedSessionFees,
        conveyanceFeePercentage,
        shouldApplyPaperDiscount,
        paperDiscountPercentage,
        currentPaperSequence,
      });

    try {
      const breakdown = buildPaymentBreakdown({
        isAdditionalPayment,
        formData,
        registrationFee,
        hasPostPaymentCategoryOrTypeChange,
        shouldApplyPaperDiscount,
        paperDiscountPercentage,
        currentPaperSequence,
        currency,
        accompanyingPersons,
        accompanyingPersonFee,
        selectedFocusedSessions,
        focusedSessionFees,
        focusedSessions,
        conveyanceFeePercentage,
      });

      if (amountInSmallestUnit === 0 || actualAmount === 0) {
        alert(
          "✅ No payment required. All items are either already paid or free."
        );
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      logPaymentBreakdownSummary({
        breakdown,
        actualAmount,
        currency,
        amountInSmallestUnit,
      });

      const orderRequestPayload = buildPaymentOrderPayload({
        amountInSmallestUnit,
        currency,
        registrationId,
        email: formData.email,
        conference: formData.conference || conferenceName,
        breakdown,
      });

      const orderResult = await createValidatedPaymentOrder({
        orderUrl,
        payload: orderRequestPayload,
        registrationDbId,
        createPaymentOrder,
        razorpayKeyId,
      });

      if (!orderResult.ok) {
        const errorText = orderResult.errorText;
        console.error("Order creation failed:", errorText);
        alert(`Unable to create payment order: ${errorText}`);
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      const order = orderResult.order;
      await loadRazorpayScript();

      const paymentSuccessHandler = isProduction
        ? async function () {
            alert("Payment successful! Processing registration...");
            setTimeout(async () => {
              alert("Registered successfully. Details Emailed");

              if (isPostPaymentAbstractEnabled(abstractStepNumber)) {
                try {
                  await checkExistingRegistration(formData.email, conferenceName);
                  setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
                } catch (error) {
                  console.error("Error reloading registration:", error);
                  setFormData((prev) => ({ ...prev, paid: true }));
                  setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
                }
              } else {
                window.location.href = "/";
              }
            }, 500);
          }
        : async function (response) {
            try {
              if (razorpayInstanceRef.current) {
                razorpayInstanceRef.current = null;
              }
              razorpayOpenRef.current = false;

              if (
                !response.razorpay_payment_id ||
                !response.razorpay_order_id ||
                !response.razorpay_signature
              ) {
                throw new Error("Incomplete payment response from Razorpay");
              }

              let handlerRegistrationId = registrationId;
              let handlerRegistrationDbId = registrationDbId;

              if (!handlerRegistrationId) {
                try {
                  const conferenceRegistrationData =
                    buildConferenceRegistrationPayload();

                  const regSaveResult =
                    await ensureRegistrationForPaymentHandler({
                      conferenceRegistrationUrl,
                      payload: conferenceRegistrationData,
                      formData,
                      saveConferenceRegistration,
                    });

                  if (regSaveResult.ok) {
                    const savedReg = regSaveResult.savedRegistration;
                    handlerRegistrationId = regSaveResult.registrationId;
                    handlerRegistrationDbId = regSaveResult.registrationDbId;

                    setFormData((prev) => ({
                      ...prev,
                      id: savedReg.registrationId,
                      regId: savedReg.dbId || savedReg.id,
                    }));
                  } else {
                    const regError = regSaveResult.errorMessage;
                    console.error("Failed to save registration:", regError);
                    throw new Error(`Failed to save registration: ${regError}`);
                  }
                } catch (regError) {
                  console.error(
                    "Error saving registration before payment:",
                    regError
                  );
                  throw new Error(
                    `Could not save registration before payment: ${regError.message}`
                  );
                }
              }

              try {
                await saveSuccessfulPaymentWithBreakdown({
                  paymentSaveUrl,
                  order,
                  response,
                  registrationDbId: handlerRegistrationDbId,
                  amountInSmallestUnit,
                  currency,
                  savePaymentWithBreakdown,
                });
              } catch (paymentError) {
                console.warn("Error saving payment entity:", paymentError);
              }

              const confirmResult = await confirmRegistration({
                registrationBaseUrl,
                registrationId: handlerRegistrationId,
              });

              handlePaymentConfirmationResult({
                confirmResult,
                abstractStepNumber,
                setFormData,
                setStep,
                showAlert: alert,
              });
            } catch (err) {
              console.error("Error in payment handler:", err);

              try {
                await captureFailedPayment(
                  {
                    code: "PROCESSING_ERROR",
                    description: err.message,
                    source: "payment_handler",
                    step: "payment_processing",
                    reason: "processing_failed",
                  },
                  order
                );
              } catch (captureError) {
                console.warn(
                  "Failed to capture payment processing error:",
                  captureError
                );
              }

              setPaymentInitiated(false);
              alert("Payment processing failed. Please try again after sometime.");
              setTimeout(() => {
                setStep(5);
              }, 1000);
            }
          };

      const options = buildRazorpayOptions({
        key: razorpayKeyId,
        order,
        currency,
        registrationDbId,
        fallbackRegistrationDbId: formData.regId,
        formData,
        handler: paymentSuccessHandler,
      });

      const rzp = new window.Razorpay(options);
      razorpayInstanceRef.current = rzp;

      rzp.on("payment.failed", async function (response) {
        console.error("Payment failed:", response.error);

        if (paymentFailureHandledRef.current) {
          return;
        }
        paymentFailureHandledRef.current = true;
        razorpayOpenRef.current = false;

        try {
          if (razorpayInstanceRef.current) {
            razorpayInstanceRef.current.close();
            razorpayInstanceRef.current = null;
          }
        } catch (closeError) {
          console.warn("Could not close Razorpay popup:", closeError);
        }

        if (paymentErrorShown) {
          return;
        }
        setPaymentErrorShown(true);

        try {
          await captureFailedPayment(response.error, order);
        } catch (captureError) {
          console.warn(
            "Failed to capture failed payment details:",
            captureError
          );
        }

        setPaymentInitiated(false);
        setTimeout(() => {
          if (paymentFailureHandledRef.current) {
            alert(
              "Payment failed. Please try again after sometime or use a different payment method."
            );

            setTimeout(() => {
              setStep(5);
            }, 500);
          }
        }, 200);
      });

      if (razorpayOpenRef.current) {
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      razorpayOpenRef.current = true;
      rzp.open();
    } catch (err) {
      console.error("Payment error", err);

      if (!paymentFailureHandledRef.current && !paymentErrorShown) {
        paymentFailureHandledRef.current = true;
        setPaymentErrorShown(true);
        razorpayOpenRef.current = false;

        try {
          await captureFailedPayment(
            {
              code: "INITIALIZATION_ERROR",
              description: err.message,
              source: "payment_initialization",
              step: "payment_setup",
              reason: "initialization_failed",
            },
            {
              currency,
              amount: amountInSmallestUnit,
            }
          );
        } catch (captureError) {
          console.warn(
            "Failed to capture payment initialization error:",
            captureError
          );
        }

        setTimeout(() => {
          alert(
            "Payment setup failed. Please try again after sometime or check your details."
          );

          setTimeout(() => {
            setStep(1);
          }, 500);
        }, 200);
      }

      setPaymentInitiated(false);
    } finally {
      setProcessingPayment(false);
      if (razorpayOpenRef.current) {
        razorpayOpenRef.current = false;
      }
    }
  };

  return {
    processingPayment,
    paymentInitiated,
    paymentErrorShown,
    startPayment,
  };
};
